import {
  isPhotoCheckOn,
  photoCheckTasks
} from '@/services/photo-check/photo-check-tasks';
import CLButton from '@/components/ui-controls/default-ui-controls/button';
import { useRunChecklistStore } from '@/stores/run/run-checklist.store';
import { useRunPhotoSessionStore } from '@/stores/run/run-photo-session.store';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import RunPhotoCheckPanel from './run-photo-check-panel.component';

/**
 * One camera for the whole run, at the top of the task list. The person photographs what they finished;
 * the photo is checked against every task, and only the tasks it shows are ticked (or marked Incomplete).
 * Tasks not in the photo are left as they are, so several photos can be taken one after another.
 */
export default function RunPhotoCheckBar() {
  const [isSingleResponse, runChecklistTree] = useRunChecklistStore(
    s => [s.isSingleResponse, s.runChecklistTree],
    shallow
  );
  const [open, setOpen] = useState(false);
  // Photos already checked in this run (the popup reopens on them)
  const runKey = String(
    runChecklistTree?.checklist?.run_id ||
      runChecklistTree?.checklist?.id ||
      'run'
  );
  const earlier = useRunPhotoSessionStore(
    s => s.sessions[runKey]?.checked.length || 0
  );

  if (isSingleResponse || !isPhotoCheckOn(runChecklistTree)) return <></>;
  const checks = photoCheckTasks(runChecklistTree);
  if (!runChecklistTree || !checks.length) return <></>;

  return (
    <>
      <div className="mx-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-black/10 bg-[#fff8ee] px-4 py-3">
        <div className="flex items-start gap-3 flex-1">
          <svg
            className="shrink-0 mt-0.5"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="black"
            strokeOpacity="0.7"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          <div className="text-sm">
            <p className="font-medium">Done with a task? Take a photo.</p>
            <p className="text-black/60 text-xs mt-0.5">
              Take a photo and AI ticks the tasks it can see. Anything not done
              is marked Incomplete, and if AI isn&apos;t sure, you decide. One
              photo can cover several tasks.
            </p>
          </div>
        </div>
        <CLButton
          className="shrink-0 flex items-center justify-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium bg-[#ffa726] border border-gray-700 hover:brightness-95"
          onClick={() => setOpen(true)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          {earlier ? `Photos (${earlier}) · Add more` : 'Take photo'}
        </CLButton>
      </div>
      {open && (
        <RunPhotoCheckPanel
          section={runChecklistTree}
          checks={checks}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
